<template>
    <div class="text-field" :class="{ 'text-field--has-error': hasError, 'text-field--success': success }">
        <slot name="customErrorTop" />
        <label
            v-if="label"
            class="mb-2"
            :for="id"
            :aria-disabled="disabled"
            :style="{ fontSize: labelFontSize }"
            :class="labelClass ? labelClass : 'caption'"
            v-text="label"
        />
        <div class="position-relative">
            <component
                :is="iconBefore"
                v-if="iconBefore"
                class="text-field__icon text-field__icon--before position-absolute"
            />
            <textarea
                v-if="multiline"
                :id="id"
                :class="[
                    'text-field-input',
                    'subtitle-1 w-100',
                    { 'icon-before-padding': iconBefore },
                    { filled: value && activeFilledState },
                ]"
                :value="value"
                :placeholder="placeholder"
                :required="required"
                :disabled="disabled"
                :min="preventNegative ? '0' : null"
                @input="handleInput"
                @blur="$emit('blur', $event)"
                @keydown="preventNegativeInput"
            ></textarea>
            <input
                v-else
                :id="id"
                :class="[
                    'text-field-input',
                    'subtitle-1 w-100',
                    { 'icon-before-padding': iconBefore },
                    { 'icon-after-padding': iconAfter },
                    { filled: value && activeFilledState },
                ]"
                :value="value"
                :type="type"
                :placeholder="placeholder"
                :required="required"
                :disabled="disabled"
                :min="preventNegative ? '0' : null"
                @input="handleInput"
                @blur="$emit('blur', $event)"
                @keydown="preventNegativeInput"
            />
            <icon-check v-if="success" class="text-field__icon text-field__success-icon position-absolute" />
            <div
                v-if="iconAfter"
                class="text-field__icon text-field__icon--after position-absolute"
                :class="{ iconAfterDisabled: disabled }"
                @click="handleIconAfterClick"
            >
                <component :is="iconAfter" />
            </div>
            <button
                v-if="iconClose"
                class="text-field__icon text-field__icon--after position-absolute"
                @click="$emit('clear')"
            >
                <icon-close height="16" width="12" />
            </button>
        </div>
        <div v-if="maxLength && multiline" :aria-disabled="disabled" class="character-count">
            {{ value.length }} / {{ maxLength }} Characters
        </div>
        <div v-if="hasError">
            <p v-for="(e, i) in errorMessages" :key="i" class="text-field__error caption mt-2">
                {{ e }}
            </p>
        </div>
        <slot name="customError" />
        <div v-if="messages.length">
            <p v-for="(m, i) in messages" :key="i" class="caption mt-2" v-text="m" />
        </div>
    </div>
</template>

<script>
import IconCheck from '@/components/Icon/Check.vue';
import IconClose from '@/components/Icon/Close.vue';
import IconSearch from '@/components/Icon/Search.vue';

export default {
    name: 'TextField',

    components: {
        IconCheck,
        IconSearch,
        IconClose,
    },

    props: {
        id: {
            type: String,
            required: true,
        },
        preventNegative: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        errorMessages: {
            type: Array,
            required: false,
            default: () => {
                return [];
            },
        },
        label: {
            type: String,
            required: false,
            default: '',
        },
        labelFontSize: {
            type: String,
            default: '14px',
        },
        labelClass: {
            type: String,
            default: '',
        },
        messages: {
            type: Array,
            required: false,
            default: () => {
                return [];
            },
        },
        placeholder: {
            type: String,
            required: false,
            default: '',
        },
        required: {
            type: Boolean,
            required: false,
            default: false,
        },
        success: {
            type: Boolean,
            required: false,
            default: false,
        },
        iconClose: {
            type: Boolean,
            required: false,
            default: false,
        },
        type: {
            type: String,
            required: false,
            default: 'text',
        },
        value: {
            type: [Number, String],
            required: false,
            default: '',
        },
        iconBefore: {
            type: Object,
            required: false,
            default: null,
        },
        iconAfter: {
            type: Object,
            required: false,
            default: null,
        },
        onIconAfterClick: {
            type: Function,
            required: false,
            default: () => {},
        },
        activeFilledState: {
            type: Boolean,
            required: false,
        },
        multiline: {
            type: Boolean,
            default: false,
        },
        maxLength: {
            type: Number,
            default: 400,
        },
    },

    computed: {
        hasError() {
            if (this.success) {
                return false;
            }

            return this.errorMessages.length > 0;
        },
    },

    methods: {
        handleInput(e) {
            if (this.preventNegative && e.target.value < 0) {
                return;
            }

            if (this.multiline) {
                const { target } = e;
                if (!this.maxLength || e.target.value.length < this.maxLength) {
                    target.style.height = 'auto';
                    target.style.height = `${e.target.scrollHeight}px`;
                    target.style.overflowY = 'hidden';
                } else {
                    target.style.overflowY = 'scroll';
                }
            }

            this.$emit('input', e.target.value);
        },
        preventNegativeInput(e) {
            if (this.preventNegative && e.key === '-') {
                e.preventDefault();
            }
        },
        handleIconAfterClick() {
            if (!this.disabled) {
                this.onIconAfterClick?.();
            }
        },
    },
};
</script>

<style scoped>
.text-field input,
.text-field textarea {
    background-color: #f9f9f9;
    border: 1px solid #a0a6a9;
    border-radius: 4px;
    color: var(--color-text);
    padding: 10px 12px 10px;
    height: 40px;
}

.text-field input.filled,
.text-field textarea.filled {
    background-color: var(--color-white);
}

.text-field input:focus-visible,
.text-field textarea:focus-visible {
    background-color: #e9f0fd;
    border-width: 2px;
    border-color: #737373;
    outline: 0;
    padding: 9px 12px 8px;
}

.text-field label {
    color: var(--color-text);
    display: block;
}

.text-field--has-error input,
.text-field--has-error textarea {
    background-color: #fbf2f2;
    border-color: var(--color-red-warning);
}

.text-field__error {
    color: var(--color-red-warning);
}

.text-field__icon {
    top: 50%;
    transform: translateY(-50%);
}

.text-field__success-icon {
    color: #38b89f;
    right: 8px;
}

.text-field__icon--before {
    left: 12px;
}
.text-field__icon--after {
    right: 12px;
}
.iconAfterDisabled {
    color: var(--Colors-Primary-Grayscale-Gray);
}
.text-field__icon--after:hover {
    cursor: pointer;
}
.text-field input.icon-before-padding,
.text-field textarea.icon-before-padding {
    padding-left: 34px;
}

.text-field input.icon-after-padding,
.text-field textarea.icon-after-padding {
    padding-right: 34px;
}

.text-field input.icon-before-padding:focus-visible,
.text-field textarea.icon-before-padding:focus-visible {
    padding-left: 33px;
}

.text-field input.icon-after-padding:focus-visible,
.text-field textarea.icon-after-padding:focus-visible {
    padding-right: 33px;
}

.text-field label[aria-disabled='true'] {
    color: #b7b6b5;
}

.text-field input:disabled,
.text-field textarea:disabled {
    border: 0.5px solid #bdbdbd;
    background: #f9f9f9;
    color: #b7b6b5;
    cursor: not-allowed;
}

.text-field label[aria-disabled='true'] {
    color: #b7b6b5;
}

.text-field input:disabled {
    border: 0.5px solid #bdbdbd;
    background: #f9f9f9;
    color: #b7b6b5;
    cursor: not-allowed;
}

.text-field textarea {
    min-height: 134px;
}

.character-count {
    font-size: 15px;
    color: #313131;
    text-align: left;
}

.character-count[aria-disabled='true'] {
    color: var(--Colors-Primary-Grayscale-Gray);
}
</style>
